/**
 * PATH: packages/auth/src/identityHygiene.ts
 * FILE_ID: AUTH-IDENTITY-HYGIENE-001
 * PURPOSE: A tiny dependency-injection seam that lets the auth STORE
 *          (performSetFromSSO / performCheckAuth) run the same
 *          "wipe per-user storage on an identity switch" routine the
 *          tenant auth gate already runs — WITHOUT @quotech/auth taking
 *          a dependency on @quotech/ui (where the hygiene routine lives).
 * WHY:     `enforceSessionHygiene` (packages/ui/src/auth/sessionHygiene.ts)
 *          is the single source of truth for wiping the previous user's
 *          per-user/per-tenant localStorage keys (profile pill, last
 *          kanban board, persisted tab strip, tenant pin, ...). The
 *          tenant gate (createApiAuthGate.applySession) calls it on
 *          every authenticated session apply, so SAM/Newmark/Steel get
 *          same-tab hygiene for free. The MASTER SPA's store-driven
 *          identity switches (UnifiedSSOPage → setFromSSO, and the
 *          checkAuth cold-load mismatch branch added in
 *          BUG-2026-06-02) only BROADCAST the change cross-tab — the
 *          tab that performed the switch never wiped its own stale
 *          per-user keys. @quotech/ui depends on @quotech/auth, so
 *          @quotech/auth importing `enforceSessionHygiene` directly
 *          would be a circular dependency. Instead the master app
 *          registers it once at boot via `setOnIdentitySwitch`, and the
 *          store calls `runOnIdentitySwitch` at the switch seams.
 * FLOW:
 *   ┌──────────────────┐ register  ┌──────────────────┐  call   ┌──────────────────┐
 *   │ app boot (main)  │ ────────► │ module-scope ref  │ ◄───── │ store switch seam │
 *   │ enforceSession   │           │ (this file)       │         │ runOnIdentity     │
 *   │ Hygiene          │           └──────────────────┘         │ Switch(...)       │
 *   └──────────────────┘                                         └──────────────────┘
 * DEPENDENCIES:
 *   - (none) — pure module-scope registry. The injected function is
 *     supplied by the consuming app; this file never imports it.
 * RELATED:
 *   - packages/ui/src/auth/sessionHygiene.ts: the routine that gets
 *     injected here (enforceSessionHygiene). Lives in @quotech/ui
 *     because the tenant gate is the chokepoint every tenant SPA goes
 *     through; the master SPA opts in via this seam.
 *   - auth-store/authActions.ts: the two call sites (performSetFromSSO
 *     identity-switch + performCheckAuth identity-mismatch).
 *   - identityReconcile.ts: decides WHETHER an identity switch happened;
 *     this module decides WHAT to do about it (the cleanup side effect).
 */

/**
 * Side effect invoked when the auth store detects the signed-in user
 * changed within this tab. Receives the new user identity (id or email,
 * whatever the store has) plus a structured reason for logs. Must never
 * throw — the runner below swallows errors, but keep the contract
 * explicit so injected implementations don't rely on the swallow.
 */
export type IdentitySwitchHandler = (
  newIdentity: string | number | null | undefined,
  context: { reason: string },
) => void

let registeredHandler: IdentitySwitchHandler | null = null

/**
 * Register the identity-switch side effect (the master SPA injects
 * `enforceSessionHygiene` here at boot). Passing `null` unregisters —
 * handy for tests and for tearing down in HMR. Idempotent: the last
 * registration wins, so calling it twice at boot is harmless.
 */
export function setOnIdentitySwitch(handler: IdentitySwitchHandler | null): void {
  registeredHandler = handler
}

/**
 * Invoke the registered identity-switch handler, if any. No-op when
 * nothing is registered (e.g. a hub that relies on the tenant gate's
 * own hygiene, or a test that didn't opt in). Never throws — a broken
 * hygiene implementation must NOT break the auth flow that triggered
 * it. Returns whether a handler actually ran (useful for tests).
 */
export function runOnIdentitySwitch(
  newIdentity: string | number | null | undefined,
  reason: string,
): boolean {
  if (!registeredHandler) return false
  try {
    registeredHandler(newIdentity, { reason })
    return true
  } catch {
    // Hygiene is a best-effort cleanup; never let it break auth.
    return false
  }
}

/** Test-only accessor — true when a handler is currently registered. */
export function hasIdentitySwitchHandler(): boolean {
  return registeredHandler !== null
}
